import React, { useEffect } from 'react';

const CachingStrategies = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Caching Strategies: LRU, LFU, TTL - System Design Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master caching strategies for high-performance systems. Learn LRU, LFU, TTL, write-through, write-back, and write-around patterns with real-world examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master caching strategies for high-performance systems. Learn LRU, LFU, TTL, write-through, write-back, and write-around patterns with real-world examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Caching Strategies for High-Performance Systems</h1>
        <p style={styles.subtitle}>
          Master LRU, LFU, TTL, and advanced caching patterns to optimize system performance
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Why Caching Matters</h2>
          <p style={styles.paragraph}>
            Caching is one of the most effective ways to improve system performance. By storing frequently accessed data 
            in fast storage (memory), we can reduce latency from milliseconds to microseconds and decrease load on backend 
            systems. A well-implemented cache can improve response times by 10-100x and reduce database load by 80-95%.
          </p>
          <p style={styles.paragraph}>
            Caching works by storing copies of data closer to where it's needed, whether that's in memory, on SSDs, or 
            at edge locations around the world. The key is choosing the right caching strategy based on your access patterns, 
            consistency requirements, and performance goals.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Cache Eviction Strategies</h2>
          
          <h3 style={styles.heading}>LRU (Least Recently Used)</h3>
          <p style={styles.paragraph}>
            LRU evicts the least recently accessed items first. This strategy works well for temporal locality patterns 
            where recently accessed data is likely to be accessed again soon. LRU is the most commonly used eviction 
            strategy because it balances simplicity with effectiveness.
          </p>
          <pre style={styles.codeBlock}>
{`class LRUCache:
    def __init__(self, capacity):
        self.capacity = capacity
        self.cache = {}  # key -> node
        self.head = DoublyLinkedNode()  # dummy head
        self.tail = DoublyLinkedNode()  # dummy tail
        self.head.next = self.tail
        self.tail.prev = self.head
    
    def get(self, key):
        if key in self.cache:
            node = self.cache[key]
            self._move_to_head(node)  # Mark as recently used
            return node.value
        return None
    
    def put(self, key, value):
        if key in self.cache:
            node = self.cache[key]
            node.value = value
            self._move_to_head(node)
        else:
            if len(self.cache) >= self.capacity:
                # Evict least recently used
                tail_node = self.tail.prev
                self._remove_node(tail_node)
                del self.cache[tail_node.key]
            
            new_node = DoublyLinkedNode(key, value)
            self.cache[key] = new_node
            self._add_to_head(new_node)

# Time Complexity: O(1) for both get and put operations`}
          </pre>

          <h3 style={styles.heading}>LFU (Least Frequently Used)</h3>
          <p style={styles.paragraph}>
            LFU evicts items with the lowest access frequency. This strategy is better for scenarios with clear access 
            patterns where some items are consistently more popular than others. LFU requires more memory overhead to 
            track frequency counts but can be more effective than LRU for certain workloads.
          </p>
          <pre style={styles.codeBlock}>
{`class LFUCache:
    def __init__(self, capacity):
        self.capacity = capacity
        self.cache = {}  # key -> (value, frequency)
        self.frequencies = {}  # frequency -> set of keys
        self.min_frequency = 0
    
    def get(self, key):
        if key not in self.cache:
            return None
        
        value, freq = self.cache[key]
        self._update_frequency(key, freq)
        return value
    
    def put(self, key, value):
        if self.capacity <= 0:
            return
        
        if key in self.cache:
            _, freq = self.cache[key]
            self.cache[key] = (value, freq)
            self._update_frequency(key, freq)
        else:
            if len(self.cache) >= self.capacity:
                self._evict_lfu()
            
            self.cache[key] = (value, 1)
            self._add_to_frequency(key, 1)
            self.min_frequency = 1
    
    def _evict_lfu(self):
        # Remove one key with minimum frequency
        key_to_remove = self.frequencies[self.min_frequency].pop()
        del self.cache[key_to_remove]`}
          </pre>

          <h3 style={styles.heading}>TTL (Time To Live)</h3>
          <p style={styles.paragraph}>
            TTL-based caching expires items after a specified time period. This strategy is useful for data that becomes 
            stale over time and needs to be refreshed periodically. TTL caching is often combined with other eviction 
            strategies to handle both space and time constraints.
          </p>
          <pre style={styles.codeBlock}>
{`import time
from threading import Timer

class TTLCache:
    def __init__(self):
        self.cache = {}  # key -> (value, expiry_time)
        self.timers = {}  # key -> timer
    
    def put(self, key, value, ttl_seconds):
        # Cancel existing timer if any
        if key in self.timers:
            self.timers[key].cancel()
        
        expiry_time = time.time() + ttl_seconds
        self.cache[key] = (value, expiry_time)
        
        # Set up expiration timer
        timer = Timer(ttl_seconds, self._expire_key, [key])
        self.timers[key] = timer
        timer.start()
    
    def get(self, key):
        if key not in self.cache:
            return None
        
        value, expiry_time = self.cache[key]
        if time.time() > expiry_time:
            self._expire_key(key)
            return None
        
        return value
    
    def _expire_key(self, key):
        if key in self.cache:
            del self.cache[key]
        if key in self.timers:
            del self.timers[key]`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Cache Write Strategies</h2>
          
          <h3 style={styles.heading}>Write-Through Cache</h3>
          <p style={styles.paragraph}>
            Write-through caching writes data to both the cache and the database simultaneously. This ensures data 
            consistency between the cache and the persistent store, but results in higher write latency since both 
            operations must complete before the write is considered successful.
          </p>
          <pre style={styles.codeBlock}>
{`def write_through(key, value):
    # Write to cache
    cache.put(key, value)
    # Write to database (synchronous)
    database.save(key, value)
    
Pros: Data consistency, cache always up-to-date
Cons: Higher write latency, database load on every write`}
          </pre>

          <h3 style={styles.heading}>Write-Back (Write-Behind) Cache</h3>
          <p style={styles.paragraph}>
            Write-back caching writes data to the cache first and updates the database asynchronously later. This provides 
            lower write latency but introduces the risk of data loss if the cache fails before the data is persisted to 
            the database.
          </p>
          <pre style={styles.codeBlock}>
{`def write_back(key, value):
    # Write to cache immediately
    cache.put(key, value)
    # Mark as dirty for later database sync
    cache.mark_dirty(key)
    
# Asynchronous background process
def sync_dirty_data():
    for key in cache.get_dirty_keys():
        value = cache.get(key)
        database.save(key, value)
        cache.clear_dirty(key)

Pros: Low write latency, reduced database load
Cons: Risk of data loss, complexity in failure handling`}
          </pre>

          <h3 style={styles.heading}>Write-Around Cache</h3>
          <p style={styles.paragraph}>
            Write-around caching bypasses the cache for writes and goes directly to the database. The cache is only 
            populated on reads (cache misses). This strategy avoids cache pollution from infrequently accessed writes 
            but may result in read misses for recently written data.
          </p>
          <pre style={styles.codeBlock}>
{`def write_around(key, value):
    # Write directly to database, bypass cache
    database.save(key, value)
    # Optionally invalidate cache entry
    cache.invalidate(key)

def read_with_cache(key):
    value = cache.get(key)
    if value is None:
        value = database.get(key)
        if value is not None:
            cache.put(key, value)
    return value

Pros: Avoids cache pollution from infrequent writes
Cons: Read miss penalty for recently written data`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Caching Patterns</h2>
          
          <h3 style={styles.heading}>Cache-Aside (Lazy Loading)</h3>
          <p style={styles.paragraph}>
            Cache-aside is the most common caching pattern where the application manages the cache explicitly. The 
            application first checks the cache, and if the data isn't found (cache miss), it fetches from the database 
            and populates the cache for future requests.
          </p>
          <pre style={styles.codeBlock}>
{`def get_user(user_id):
    # Try cache first
    user = cache.get(f"user:{user_id}")
    if user is not None:
        return user
    
    # Cache miss - fetch from database
    user = database.get_user(user_id)
    if user is not None:
        # Store in cache with TTL
        cache.put(f"user:{user_id}", user, ttl=3600)
    
    return user

def update_user(user_id, user_data):
    # Update database
    database.update_user(user_id, user_data)
    # Invalidate cache
    cache.delete(f"user:{user_id}")`}
          </pre>

          <h3 style={styles.heading}>Multi-Level Caching</h3>
          <p style={styles.paragraph}>
            Multi-level caching uses multiple cache layers with different characteristics. For example, L1 cache might 
            be in-memory for fastest access, L2 cache might be Redis for larger capacity, and L3 cache might be SSD-based 
            for persistence across restarts.
          </p>
          <pre style={styles.codeBlock}>
{`class MultiLevelCache:
    def __init__(self):
        self.l1_cache = LRUCache(1000)      # In-memory, fast
        self.l2_cache = RedisCache()        # Network, medium speed
        self.l3_cache = SSDCache()          # Disk, slower but large
    
    def get(self, key):
        # Try L1 first
        value = self.l1_cache.get(key)
        if value is not None:
            return value
        
        # Try L2
        value = self.l2_cache.get(key)
        if value is not None:
            self.l1_cache.put(key, value)  # Promote to L1
            return value
        
        # Try L3
        value = self.l3_cache.get(key)
        if value is not None:
            self.l1_cache.put(key, value)  # Promote to L1
            self.l2_cache.put(key, value)  # Promote to L2
            return value
        
        return None  # Cache miss at all levels`}
          </pre>

          <h3 style={styles.heading}>Cache Warming</h3>
          <p style={styles.paragraph}>
            Cache warming involves proactively populating the cache with likely-to-be-accessed data before users request it. 
            This reduces cache misses and improves user experience, especially after cache restarts or during predictable 
            traffic spikes.
          </p>
          <pre style={styles.codeBlock}>
{`def warm_user_cache():
    """Warm cache with recently active users"""
    recent_users = database.get_recent_active_users(limit=1000)
    
    for user in recent_users:
        cache.put(f"user:{user.id}", user, ttl=3600)
        
        # Also warm related data
        user_preferences = database.get_user_preferences(user.id)
        cache.put(f"preferences:{user.id}", user_preferences, ttl=7200)

def warm_popular_content():
    """Warm cache with trending content"""
    trending = analytics.get_trending_content(last_hours=24)
    
    for content_id in trending:
        content = database.get_content(content_id)
        cache.put(f"content:{content_id}", content, ttl=1800)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Facebook's TAO</h3>
          <p style={styles.paragraph}>
            Facebook uses a write-through cache for social graph data with TTL-based expiration. They cache user profiles, 
            friendships, and posts with different TTL values based on access patterns. Popular content gets longer TTL 
            values while less popular content expires more quickly.
          </p>

          <h3 style={styles.heading}>Netflix's EVCache</h3>
          <p style={styles.paragraph}>
            Netflix uses a distributed caching solution with LRU eviction for movie metadata, user preferences, and 
            recommendation data. They employ cache warming for popular content during peak hours and use multiple cache 
            layers for different types of data.
          </p>

          <h3 style={styles.heading}>Twitter's Timeline Caching</h3>
          <p style={styles.paragraph}>
            Twitter uses a combination of write-through (for celebrity tweets) and cache-aside (for regular users) patterns 
            with TTL-based expiration for timeline data. They pre-compute and cache timelines for active users while using 
            on-demand computation for less active users.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right eviction policy:</strong> Use LRU for temporal locality, LFU for frequency patterns, 
              and TTL for time-sensitive data.
            </li>
            <li style={styles.listItem}>
              <strong>Set appropriate TTL values:</strong> Balance between data freshness and cache hit rates based on 
              your data's natural update frequency.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor cache metrics:</strong> Track hit rates, miss rates, eviction rates, and memory usage to 
              optimize cache performance.
            </li>
            <li style={styles.listItem}>
              <strong>Handle cache failures gracefully:</strong> Always have a fallback to the data source and implement 
              circuit breakers for cache dependencies.
            </li>
            <li style={styles.listItem}>
              <strong>Use consistent serialization:</strong> Ensure data format consistency across cache operations to 
              avoid deserialization errors.
            </li>
            <li style={styles.listItem}>
              <strong>Implement cache warming:</strong> Pre-populate cache with likely-to-be-accessed data, especially 
              after restarts or during predictable traffic patterns.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Effective caching strategies are crucial for building high-performance systems. The choice between LRU, LFU, TTL, 
            and other strategies depends on your specific access patterns, data characteristics, and consistency requirements.
          </p>
          <p style={styles.paragraph}>
            Remember that caching is not just about speed - it's about reducing load on your backend systems, improving user 
            experience, and enabling your system to scale cost-effectively. Choose your caching strategy based on your specific 
            needs and always monitor cache performance to ensure it's providing the expected benefits.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CachingStrategies;